import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import {
  Shield, Truck, CheckCircle, ArrowRight,
  MapPin, ChevronRight, Star, Eye,
  Zap, Lock, HelpCircle, DollarSign, Phone
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

const HERO_PRIMARY_IMG = '/images/Hero-img.png';
const HERO_LIGHT_IMG = '/images/Hero-img-light.png';
const HERO_FALLBACK_IMG = '/images/Hero-img2.png';

function HeroQuoteCard({ theme }) {
  return (
    <div className="hidden lg:block w-full max-w-sm" data-testid="hero-quote-card">
      <div className={`hero-quote-card rounded-2xl p-6 opacity-0 animate-fade-in stagger-4 ${
        theme === 'light'
          ? 'bg-gray-800/90 border border-gray-700 shadow-lg backdrop-blur-sm'
          : ''
      }`}>
        <p className={`text-[10px] uppercase tracking-[0.25em] mb-5 font-mono ${
          theme === 'light' ? 'text-gray-300' : 'text-white/40'
        }`}>Instant Quote Preview</p>
        <div className="space-y-3">
          <div className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
            theme === 'light'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/[0.04] border-border'
          }`}>
            <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
            <div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Pickup</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Los Angeles, CA 90001</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
            theme === 'light'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/[0.04] border-border'
          }`}>
            <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
            <div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Delivery</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>New York, NY 10001</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-lg px-4 py-3 border ${
              theme === 'light'
                ? 'bg-white/10 border-white/20'
                : 'bg-white/[0.04] border-border'
            }`}>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Model</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Model Y</p>
            </div>
            <div className={`rounded-lg px-4 py-3 border ${
              theme === 'light'
                ? 'bg-white/10 border-white/20'
                : 'bg-white/[0.04] border-border'
            }`}>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Transport</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Enclosed</p>
            </div>
          </div>
          <div className={`border-t pt-4 mt-1 ${theme === 'light' ? 'border-white/20' : 'border-border'}`}>
            <div className="flex items-end justify-between mb-4">
              <div>
                <p className={`text-[10px] ${theme === 'light' ? 'text-white' : 'text-white/30'}`}>Estimated Price</p>
                <p className="text-3xl font-outfit font-bold text-white">$1,450</p>
              </div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-white' : 'text-white/30'}`}>~2,800 mi</p>
            </div>
            <Link to="/quote">
              <Button
                className="w-full bg-accent text-white hover:bg-accent/90 rounded-xl py-5 text-sm font-semibold transition-all duration-300 hover:shadow-[0_0_20px_rgba(227,25,55,0.3)]"
                data-testid="hero-card-reserve-btn"
              >
                Reserve Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [imgLoaded, setImgLoaded] = useState(false);
  const { theme } = useTheme();

  // Instant quote calculator state
  const [calcOrigin,    setCalcOrigin]    = useState('');
  const [calcDest,      setCalcDest]      = useState('');
  const [calcTransport, setCalcTransport] = useState('open');
  const [calcResult,    setCalcResult]    = useState(null);
  const [calcLoading,   setCalcLoading]   = useState(false);
  const calcTimer = useRef(null);

  // Dynamic Metadata Lifecycle Management
  useEffect(() => {
    document.title = "Tesla Shipping Service USA | Safe & Reliable EV Transport";

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }

    metaKeywords.setAttribute(
      'content',
      'Tesla shipping service USA, Tesla car shipping USA, Ship Tesla across the USA, Tesla transport company, Enclosed Tesla transport company, Electric vehicle transport USA, EV car shipping service, Tesla shipping quote online, AI-powered vehicle tracking, Tesla delivery service USA, Cross-country Tesla shipping, Electric vehicle logistics USA'
    );
  }, []);

  // Fetch live price when both ZIPs are complete
  useEffect(() => {
    if (calcOrigin.length === 5 && calcDest.length === 5 && calcOrigin !== calcDest) {
      clearTimeout(calcTimer.current);
      calcTimer.current = setTimeout(async () => {
        setCalcLoading(true);
        setCalcResult(null);
        try {
          const res = await fetch(`${API_URL}/api/quote-preview`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({
              pickup_zip:     calcOrigin,
              delivery_zip:   calcDest,
              transport_type: calcTransport,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success) setCalcResult(data);
          }
        } catch {}
        finally { setCalcLoading(false); }
      }, 700);
    } else {
      clearTimeout(calcTimer.current);
      setCalcResult(null);
    }
  }, [calcOrigin, calcDest, calcTransport]);

  const howItWorks = [
    {
      step: '01',
      title: 'Get a quote in minutes',
      description: 'Enter your vehicle, pickup and delivery addresses, and open or enclosed preference. Instant price -- no waiting, no sales calls.',
      icon: Zap,
    },
    {
      step: '02',
      title: 'Reserve with a refundable deposit',
      description: 'Your deposit is fully refundable right up until we confirm dispatch with an assigned carrier. Zero risk until you\'re sure.',
      icon: Lock,
    },
    {
      step: '03',
      title: 'Track from pickup to delivery',
      description: 'SMS and email updates from carrier assignment through delivery completion. You always know exactly where your Tesla is.',
      icon: Eye,
    },
  ];

  const whyShipTesla = [
    {
      icon: Shield,
      title: 'Verified, insured carriers',
      description: 'Every carrier is licensed, insured, and vetted to our standards before they ever touch your car. No surprises, no unknowns.',
    },
    {
      icon: Zap,
      title: 'EV-aware handling',
      description: 'Soft-strap tie-downs, careful low-clearance loading, and charge-state guidance specific to electric vehicles. Not just any carrier.',
    },
    {
      icon: DollarSign,
      title: 'Transparent pricing',
      description: 'Margin-protected dispatch means a fair price for you and reliable service from the carrier. No lowball-then-vanish tactics.',
    },
    {
      icon: Eye,
      title: 'Proactive updates',
      description: 'Real status from booking to delivery by SMS and email. You know exactly where your Tesla is every step of the way.',
    },
  ];

  const popularRoutes = [
    { from: 'California', to: 'Texas', days: '3--5 days', open: '$1,450--$1,700', enclosed: '$2,300--$2,700' },
    { from: 'California', to: 'Florida', days: '6--9 days', open: '$2,800--$3,200', enclosed: '$4,400--$5,100' },
    { from: 'Mainland', to: 'Hawaii', days: '2--4 weeks', open: 'Custom quote', enclosed: 'Custom quote' },
  ];

  const testimonials = [
    {
      rating: 5,
      text: "Shipped my Model Y from LA to Miami without a single issue. The process was incredibly smooth and transparent. Driver was professional and delivered on time.",
      name: "Alex R.",
      detail: "Model Y Owner",
    },
    {
      rating: 5,
      text: "Used ShipTesla to move my Model 3 across the country. The quote was instant, the carrier was professional, and my car arrived in perfect condition. Highly recommended!",
      name: "Miya K.",
      detail: "Model 3 Owner",
    },
    {
      rating: 5,
      text: "Enclosed transport for my Model S Plaid was worth every penny. Real-time updates the whole way. Car arrived spotless. Will use again without hesitation.",
      name: "James C.",
      detail: "Model S Owner",
    },
  ];

  const faqItems = [
    {
      q: 'How much does it cost to ship a Tesla?',
      a: 'Open transport starts at $1.05/mile + $250 base. Enclosed starts at $1.68/mile + $350 base. Example: a 1,000-mile open shipment runs ~$1,300. Get an exact quote for your route in 60 seconds.',
    },
    {
      q: 'How much does it cost to ship a Tesla cross-country?',
      a: 'A coast-to-coast move (about 2,800 miles) runs roughly $3,190 open or $5,050 enclosed at our published rates. Get an exact quote for your specific route in 60 seconds.',
    },
    {
      q: 'Is it safe to ship a Tesla?',
      a: 'Yes. With a licensed, insured, EV-aware carrier using soft-strap tie-downs, Teslas ship safely every day. Enclosed transport adds protection from weather and road debris for extra peace of mind on high-value cars.',
    },
    {
      q: 'How long does it take to ship a Tesla?',
      a: 'Most Tesla shipments take 1--9 days in transit depending on distance, plus a pickup window. Cross-country moves average 7--9 days door to door.',
    },
    {
      q: 'Is enclosed transport worth the extra cost?',
      a: 'For new, high-value, wrapped, or Performance Teslas -- or harsh-weather routes -- the added protection is usually worth it. Enclosed typically runs 30--60% more than open.',
    },
    {
      q: 'Is the deposit refundable?',
      a: 'Yes -- your deposit is fully refundable until dispatch is confirmed. Once a carrier is assigned, the deposit becomes non-refundable.',
    },
    {
      q: 'Are there any hidden fees?',
      a: 'No. Your quote is the price. The only change would be if details you provided (like operability or delivery location access) turn out to be different at pickup.',
    },
    {
      q: 'Can you ship a Tesla that won\'t drive?',
      a: 'Often yes, but inoperable vehicles need special equipment -- let us know at quote time so we assign the right carrier. Inoperable moves typically carry an additional fee.',
    },
    {
      q: 'Can you ship a Tesla to Hawaii?',
      a: 'Yes -- by combining mainland ground transport to a West Coast port with ocean RoRo or container shipping to the islands. We handle the coordination end to end. Plan for roughly 2--4 weeks total.',
    },
    {
      q: 'Should I charge my Tesla before shipping?',
      a: 'Charge your Tesla to about 50% before transport -- enough for loading and delivery handling, without the risk of a full or near-empty battery.',
    },
  ];

  const homeSchemaGraph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Tesla Shipping Services",
        "provider": {
          "@type": "Organization",
          "name": "ShipTesla"
        },
        "areaServed": {
          "@type": "Country",
          "name": "United States"
        },
        "description": "Reliable Tesla vehicle transport with enclosed and open carrier options.",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map((item) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchemaGraph) }}
      />

      <Navigation />

      {/* â"€â"€ Hero Section â"€â"€ */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-zoom">
          <img
            src={theme === 'light' ? HERO_LIGHT_IMG : HERO_PRIMARY_IMG}
            alt="Enclosed auto transport carrier loading a Tesla at sunset"
            className={`w-full h-full object-cover object-[center_60%] sm:object-center transition-opacity duration-1000 hero-image-enhance ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            loading="eager"
            data-testid="hero-image"
          />
        </div>

        {!imgLoaded && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${theme === 'light' ? HERO_LIGHT_IMG : HERO_FALLBACK_IMG}')`, filter: 'blur(4px) brightness(0.3)' }}
          />
        )}

        <div className={`absolute inset-0 ${theme === 'light' ? 'bg-white/[0.35]' : 'bg-black/[0.60]'}`} />
        <div className={`absolute inset-0 bg-gradient-to-t ${theme === 'light' ? 'from-white via-white/10 to-transparent' : 'from-[#030303] via-[#030303]/25 to-transparent'}`} />
        <div className={`absolute inset-0 bg-gradient-to-r ${theme === 'light' ? 'from-white/20 via-transparent to-transparent' : 'from-[#030303]/45 via-transparent to-transparent'}`} />
        <div className="absolute inset-0 hero-grain pointer-events-none" />
        <div className="absolute inset-0 hero-light-sweep pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="flex items-center gap-12 lg:gap-20">
            <div className="flex-1 max-w-xl">
              <p className={`text-[11px] uppercase tracking-[0.3em] font-mono mb-6 opacity-0 animate-fade-in ${
                theme === 'light' ? 'text-gray-800' : 'text-white/40'
              }`}>
                America's #1 Tesla Transport Specialist
              </p>
              <h1 className={`font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold tracking-[-0.02em] leading-[1.05] mb-6 opacity-0 animate-fade-in stagger-1 ${
                theme === 'light' ? 'text-gray-900' : 'text-white'
              }`}>
                Ship Your Tesla
                <span className="block hero-accent-text">Safely Across</span>
                the USA
              </h1>
              <p className={`text-base lg:text-lg mb-10 opacity-0 animate-fade-in stagger-2 leading-relaxed max-w-md ${
                theme === 'light' ? 'text-gray-800' : 'text-white/50'
              }`}>
                Verified, EV-aware carriers. Open or enclosed. Transparent pricing, refundable deposit. Get an instant quote in 60 seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in stagger-3 mb-10">
                <Link to="/quote">
                  <Button
                    className="bg-accent text-white hover:bg-accent/90 rounded-full px-10 py-6 text-sm font-semibold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(227,25,55,0.3)]"
                    data-testid="hero-get-quote-btn"
                  >
                    Get Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    className={`rounded-full px-10 py-6 text-sm transition-all duration-300 ${
                      theme === 'light'
                        ? 'border-gray-800 text-gray-900 hover:bg-gray-900 hover:text-white'
                        : 'border-white/15 text-white/70 hover:bg-muted/30 hover:text-white'
                    }`}
                    data-testid="hero-view-pricing-btn"
                  >
                    View Pricing
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 opacity-0 animate-fade-in stagger-4">
                {[
                  { value: '$775', label: 'Open, starting' },
                  { value: '1--9 days', label: 'Typical transit' },
                  { value: '100%', label: 'Refundable deposit' },
                  { value: 'EV-aware', label: 'Soft-strap carriers' },
                ].map((stat, i) => (
                  <div key={i} className={`rounded-xl px-3 py-3 border ${
                    theme === 'light'
                      ? 'bg-gray-800/80 border-gray-700'
                      : 'bg-white/[0.04] border-border'
                  }`}>
                    <p className="font-outfit font-bold text-white text-base">{stat.value}</p>
                    <p className="text-[10px] mt-0.5 text-gray-300">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <HeroQuoteCard theme={theme} />
          </div>
        </div>
      </section>

      {/* â"€â"€ Social Proof Strip â"€â"€ */}
      <section className="py-5 border-b border-border" data-testid="social-proof-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-[13px] text-muted-foreground">
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 fill-accent text-accent" />
              <span><span className="font-semibold text-foreground">4.9</span> Average Rating</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border" />
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <span><span className="font-semibold text-foreground">1,200+</span> Tesla Moved</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border" />
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <span><span className="font-semibold text-foreground">100%</span> Verified Carriers</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border" />
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <span><span className="font-semibold text-foreground">$1M+</span> Insurance Available</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border" />
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-accent" strokeWidth={1.5} />
              <span className="font-semibold text-foreground">Licensed &amp; Bonded</span>
            </div>
          </div>
        </div>
      </section>

      {/* â"€â"€ How Tesla Shipping Works â"€â"€ */}
      <section id="how-it-works" className="py-16" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Simple &amp; Easy Process</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              How Tesla Shipping Works
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">
              From quote to delivery, we handle everything. Most shipments are booked and dispatched within 24--48 hours.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks.map((item, i) => (
              <div key={i} className="relative group" data-testid={`how-step-${i}`}>
                <div className="bg-card border border-border group-hover:border-border/70 rounded-2xl p-8 transition-all duration-500 h-full shadow-sm">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[11px] text-muted-foreground/50">{item.step}</span>
                  </div>
                  <h3 className="font-outfit text-lg font-semibold mb-2 text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ Open vs. Enclosed Transport â"€â"€ */}
      <section className="py-16 bg-muted/30" data-testid="transport-comparison-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Transport Options</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Open vs. Enclosed Transport
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-sm leading-relaxed">
              Choose the transport type that fits your Tesla's needs and your budget. Both are safe -- it's about the level of protection you seek.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Open */}
            <div className="bg-card border border-border rounded-2xl p-8 hover:border-accent/20 transition-all duration-500 shadow-sm" data-testid="transport-open-card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    <Truck className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-foreground">Open Transport</h3>
                </div>
                <span className="text-[10px] font-semibold px-3 py-1 rounded-full bg-green-500/10 text-green-500 border border-green-500/20">
                  Best Price
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-muted-foreground mb-8">
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Best value for money</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Fastest pickup availability</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Most carrier network</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Safe for standard conditions</li>
              </ul>
              <div className="border-t border-border pt-5">
                <p className="text-xs text-muted-foreground mb-1">Starting</p>
                <p className="font-outfit text-2xl font-bold text-foreground">$1.05<span className="text-sm font-normal text-muted-foreground">/mile + $250</span></p>
              </div>
            </div>

            {/* Enclosed */}
            <div className="bg-card border border-accent/30 rounded-2xl p-8 hover:border-accent/50 transition-all duration-500 relative shadow-sm" data-testid="transport-enclosed-card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-accent" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-foreground">Enclosed Transport</h3>
                </div>
                <span className="text-[10px] font-semibold px-3 py-1 rounded-full bg-accent/10 text-accent border border-accent/20 shadow-[0_0_12px_rgba(227,25,55,0.2)]">
                  Popular
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-muted-foreground mb-8">
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Full weather protection</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Ideal for high value Teslas</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Door-to-door loading</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Maximum security &amp; privacy</li>
              </ul>
              <div className="border-t border-accent/20 pt-5">
                <p className="text-xs text-muted-foreground mb-1">Starting</p>
                <p className="font-outfit text-2xl font-bold text-foreground">$1.68<span className="text-sm font-normal text-muted-foreground">/mile + $350</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* â"€â"€ Get Your Quote in 60 Seconds â"€â"€ */}
      <section className="py-16" data-testid="quote-calculator-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div>
              <p className="overline mb-4">Instant Pricing</p>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
                Get Your Quote in<br />60 Seconds
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed mb-4">
                Our transparent pricing is based on real market rates -- what you see is exactly what you'll pay.
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed">
                No bait-and-switch tactics. Transport done right.
              </p>
            </div>

            {/* Right -- Instant Quote Calculator */}
            <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-5">
              <p className="text-[11px] uppercase tracking-[0.25em] font-mono text-muted-foreground">Instant Quote Calculator</p>

              {/* ZIP inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1.5">Origin ZIP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="e.g. 75063"
                    value={calcOrigin}
                    onChange={e => setCalcOrigin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1.5">Destination ZIP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    placeholder="e.g. 90210"
                    value={calcDest}
                    onChange={e => setCalcDest(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors"
                  />
                </div>
              </div>

              {/* Transport type toggle */}
              <div className="grid grid-cols-2 gap-3">
                {[['open', 'Open Carrier', '$199 deposit'], ['enclosed', 'Enclosed Carrier', '$299 deposit']].map(([val, label, dep]) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setCalcTransport(val)}
                    className={`rounded-xl border px-3 py-3 text-left transition-all ${
                      calcTransport === val
                        ? 'border-accent bg-accent/10 text-foreground'
                        : 'border-border hover:border-white/20 text-muted-foreground'
                    }`}
                  >
                    <p className="text-xs font-semibold">{label}</p>
                    <p className={`text-[10px] mt-0.5 ${calcTransport === val ? 'text-accent' : 'text-muted-foreground'}`}>{dep}</p>
                  </button>
                ))}
              </div>

              {/* Price result */}
              <div className="rounded-xl border border-border bg-muted/20 px-5 py-4 min-h-[80px] flex items-center">
                {calcLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground w-full justify-center">
                    <DollarSign className="h-4 w-4 animate-pulse text-accent" />
                    Getting live market price…
                  </div>
                ) : calcResult ? (
                  <div className="flex items-center justify-between w-full flex-wrap gap-2">
                    <div>
                      <p className="text-xs text-muted-foreground mb-0.5">Your Price</p>
                      <p className="font-outfit text-3xl font-bold text-foreground">
                        ${calcResult.price?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {calcResult.distance_miles?.toFixed(0)} mi · ${calcResult.deposit} deposit
                      </p>
                    </div>
                    {(() => {
                      const map = {
                        cd_market:      { label: 'CD Market Price', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
                        price_estimate: { label: 'Live Estimate',   cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20'  },
                        standard:       { label: 'Standard Price',  cls: 'bg-muted/60 text-muted-foreground border-border'   },
                      };
                      const { label, cls } = map[calcResult.pricing_source] || map.standard;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
                          <MapPin className="h-2.5 w-2.5" />
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground w-full text-center">
                    Enter both ZIP codes to see your live price
                  </p>
                )}
              </div>

              <Link to="/quote">
                <Button
                  className="w-full bg-accent text-white hover:bg-accent/90 rounded-xl py-5 text-sm font-semibold transition-all duration-300 hover:shadow-[0_0_20px_rgba(227,25,55,0.3)]"
                  data-testid="calculator-get-quote-btn"
                >
                  Get Exact Quote
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* â"€â"€ Built Exclusively for Tesla Owners â"€â"€ */}
      <section className="py-16 bg-muted/30" data-testid="why-shiptesla-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Tesla Specialists</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Built Exclusively for Tesla Owners
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto text-sm leading-relaxed">
              We're not a generic auto transport broker. Every process, carrier, and protocol is engineered specifically for electric vehicles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyShipTesla.map((item, i) => (
              <div
                key={i}
                className="bg-card border border-border hover:border-accent/20 rounded-2xl p-6 transition-all duration-500 shadow-sm"
                data-testid={`why-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit text-base font-semibold mb-2 text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ Popular Tesla Shipping Routes â"€â"€ */}
      <section className="py-16" data-testid="popular-routes-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Top Routes</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Popular Tesla Shipping Routes
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">
              We cover all 48 contiguous states plus Hawaii. Here are our most frequently booked routes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {popularRoutes.map((route, i) => (
              <div key={i} className="bg-card border border-border hover:border-accent/20 rounded-2xl p-6 transition-all duration-500 shadow-sm" data-testid={`route-card-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
                    {route.from}
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
                    {route.to}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{route.days} transit</p>
                <div className="space-y-1.5 mb-5 text-xs text-muted-foreground">
                  <div className="flex justify-between"><span>Open</span><span className="text-foreground font-medium">{route.open}</span></div>
                  <div className="flex justify-between"><span>Enclosed</span><span className="text-foreground font-medium">{route.enclosed}</span></div>
                </div>
                <Link to="/quote">
                  <Button
                    variant="outline"
                    className="w-full rounded-xl border-border hover:border-accent/30 text-sm transition-all"
                    data-testid={`route-quote-btn-${i}`}
                  >
                    Instant Quote
                    <ArrowRight className="ml-2 h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ Testimonials â"€â"€ */}
      <section className="py-16 bg-muted/30" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Customer Reviews</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              1,200+ Tesla Owners Trust Us
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">
              Don't take our word for it -- here's what Tesla owners say after shipping with us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-6 shadow-sm" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ Prepare Your Tesla â"€â"€ */}
      <section className="py-16" data-testid="prepare-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Before Pickup</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Prepare Your Tesla for Transport
            </h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">
              Four simple steps before your carrier arrives to ensure a smooth, damage-free pickup.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                step: '01',
                title: 'Charge to ~50%',
                body: 'Enough for loading and delivery handling without the risk of a full or near-empty pack. Not fully charged, not empty.',
              },
              {
                step: '02',
                title: 'Disable Sentry Mode & PIN to Drive',
                body: 'Share guidance with your carrier so they can load and secure the car without triggering alerts or lockouts.',
              },
              {
                step: '03',
                title: 'Remove personal items & accessories',
                body: 'Clear out valuables and any exterior accessories. Fold in the side mirrors for clearance on the carrier.',
              },
              {
                step: '04',
                title: 'Document existing condition',
                body: 'Note any cosmetic marks before pickup. Your driver will complete a condition report at both pickup and delivery.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-6 flex gap-4 shadow-sm">
                <span className="font-mono text-[11px] text-accent shrink-0 mt-0.5">{item.step}</span>
                <div>
                  <h3 className="font-outfit text-base font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ FAQ â"€â"€ */}
      <section className="py-16 bg-muted/30" data-testid="faq-preview-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="overline mb-4">Customer Support</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
              Everything you need to know about shipping your Tesla across the USA
            </p>
          </div>

          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* â"€â"€ Final CTA â"€â"€ */}
      <section className="py-16 bg-gradient-to-b from-muted/30 to-background" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl md:text-5xl font-bold tracking-tight mb-5 text-foreground">
            Ready to Ship Your Tesla?
          </h2>
          <p className="text-base text-muted-foreground mb-12 max-w-lg mx-auto">
            Get an instant quote in under 60 seconds. No commitment required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/quote">
              <Button
                className="bg-accent text-white hover:bg-accent/90 rounded-full px-12 py-6 text-sm font-semibold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(227,25,55,0.25)]"
                data-testid="final-cta-btn"
              >
                Get Instant Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href="tel:18007447852">
              <Button
                variant="outline"
                className="rounded-full px-10 py-6 text-sm border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all"
                data-testid="final-cta-phone-btn"
              >
                <Phone className="mr-2 h-4 w-4" />
                Call 1-800-SHIP-TSLA
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* â"€â"€ Brand Disclaimer â"€â"€ */}
      <div className="border-t border-border py-4">
        <p className="text-center text-xs text-muted-foreground/60 px-6">
          ShipTesla is not affiliated with Tesla, Inc. All price examples are illustrative -- the quote tool gives the exact price.
        </p>
      </div>

      <Footer />
    </div>
  );
}

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:border-border/70 shadow-sm"
      data-testid={`faq-item-${index}`}
    >
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-sm pr-4 text-foreground">{question}</span>
        <HelpCircle className={`h-4 w-4 text-muted-foreground/50 shrink-0 transition-transform duration-300 ${open ? 'rotate-45 text-accent' : ''}`} strokeWidth={1.5} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-72' : 'max-h-0'}`}>
        <div className="px-6 pb-5">
          <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}

